<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :items="displayedWorkOrders"
      :title="$t('labWorkOrders.pageName')"
      :isSearchable="false"
      :isInfinitScroll="true"
      :iconHeader="'save_alt'"
      :loading="loading"
      :headers="tableHeaders"
      :pagination="pagination"
      :itemActions="actions"
      :elementsPerPage="queryParams.perpage"
      :headerActionButtons="headerActionButtons"
      :actionButtonsClass="'ml-auto'"
      :sortableColumns="sortableColumns"
      isSelectable
      hideDefaultFooter
      showExpand
      isCustomIcon
      @sendQueryParams="fetchQuery"
      @send-item="onClickRedirect"
      @iconAction="onClickAction"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filterItems"
          :searchPlaceholder="$t('laboratory.placeholder.labWoSearchBy')"
          :filterName="filterName"
          :saveFilter="true"
          :datesRange="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span>{{ $t(`labWorkOrders.labels.${item.status}`) }}</span>
      </template>
      <template
        v-slot:[`item.assignedTo`]="{ item }"
      >
        <span> {{ item.assignedTo ? item.assignedTo : '' }}  </span>
      </template>

      <template #expanded-item="props">
        <td :colspan="props.headers.length">
          <VTableLocal
            :items="props.item.analysisRequests"
            disablePagination
            :headers="sampleHeaders"
          >
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ $options.filters.formatDate(item.createdAt, 'L LT') }}
            </template>
            <template v-slot:[`item.type`]="{ item }">
              {{ $t(`sampleTypes.${item.type.charAt(0).toLowerCase()}${item.type.substring(1)}`) }}
            </template>
            <template v-slot:[`item.requestedBy`]="{ item }">
              {{ `${item.requestedBy.user.firstName} ${item.requestedBy.user.lastName}` }}
            </template>
            <template v-slot:[`item.component`]="{ item }">
              {{ componentName(item,props) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              {{ $t(`laboratory.labels.${item.status.toLowerCase()}`) }}
            </template>
            <template v-slot:[`item.attached`]="{ item }">
              <v-btn
                :disabled="props.item.status ==='SENT'"
                icon
                color="#26384B"
                @click="unlink(item)"
              >
                <v-icon class="material-icons-outlined">
                  link_off
                </v-icon>
              </v-btn>
            </template>
          </VTableLocal>
        </td>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <LoadingWidget
      :show="isDownloadingReport"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import labWorkOrderStatus from '@/constants/labWorkOrderStatus';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';
import { mapGetters } from 'vuex';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import Exportation from '@/views/laboratory/Exportation.vue';
import roles from '@/constants/roles';
import filterMixin from '@/mixins/filterMixin';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import perpage from '@/constants/pagination';
import moment from 'moment';

export default {
    components: {
        VTableLocal,
        TableRemote,
        SearchWithFilters,
        LoadingWidget,
    },
    mixins: [
        filterMixin,
    ],
    data() {
        return {
            isDownloadingReport: false,
            isInitializing: false,
            workOrders: [],
            loading: false,
            pagination: {},
            filterName: 'workOrders',
            queryParams: {
                perpage,
                completionStatus: ['TODO', 'INPROGRESS'],
            },
            filters: [
                {
                    key: 'completionStatus',
                    label: 'labWorkOrders.labels.status',
                    type: 'select',
                    value: ['TODO', 'INPROGRESS'],
                    multiple: true,
                    clearable: true,
                    items: Object.values(labWorkOrderStatus).map(status => ({
                        text: `labWorkOrders.labels.${status}`,
                        value: status,
                    })),
                },
                {
                    key: 'date',
                    label: 'forms.labels.createdAt',
                    type: 'date',
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    dataCY: 'workOrderCreatedAt',
                },
                {
                    key: 'isRepRecommendationRequested',
                    label: 'labWorkOrders.labels.comments',
                    type: 'select',
                    value: '',
                    multiple: false,
                    clearable: true,
                    items: ['recommendation'].map(status => ({
                        text: `labWorkOrders.labels.${status}`,
                        value: false,
                    })),
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'hasRole',
            'hasPermission',
        ]),
        status() {
            return labWorkOrderStatus;
        },
        headers() {
            const mobileHeaders = [
                'clientName',
                'workOrder',
                'assignedTo',
                'status',
            ];

            const headers = [
                'clientName',
                'clientSite',
                'creationDate',
                'completedDate',
                'workOrder',
                'assignedTo',
                'soNumber',
                'nbAnalysis',
                'status',
            ];

            return this.$vuetify.breakpoint.mdAndDown ? mobileHeaders : headers;
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`labWorkOrders.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
        displayedWorkOrders() {
            return this.workOrders.map(workOrder => ({
                ...workOrder,
                creationDate: this.$options.filters.formatDate(workOrder.createdAt, 'L LT'),
                completedDate: this.$options.filters.formatDate(workOrder.completedAt, 'L LT'),
                workOrder: workOrder.woNumber,
                clientName: `${workOrder.client.name} [${workOrder.client.accountNumber}]`,
                clientSite: `${workOrder.site.name} [${workOrder.site.deliveryAccountNumber}]`,
                assignedTo: this.getAssignedTo(workOrder.assignedTo),
                poNumber: workOrder.poNumber || '',
                soNumber: workOrder.soNumber || '',
                nbAnalysis: workOrder.nbAnalysisRequests || 0,
                showWorkSheet: [labWorkOrderStatus.TODO, labWorkOrderStatus.IN_PROGRESS].includes(workOrder.status),
            }));
        },
        sampleHeaders() {
            return [
                'requestNumber',
                'poNumber',
                'type',
                'component',
                'createdAt',
                'requestedBy',
                'status',
                'attached',
            ].map(value => ({
                text: this.$t(`analysisRequests.labels.${value}`),
                value,
            }));
        },
        isRep() {
            return this.hasRole(roles.SALES_REPRESENTATIVE);
        },
        isSalesManager() {
            return this.hasRole(roles.SALES_MANAGER);
        },
        filterItems() {
            return (this.isRep || this.isSalesManager) ? [
                {
                    key: 'isRepRecommendationRequested',
                    label: 'labWorkOrders.labels.comments',
                    type: 'select',
                    value: '',
                    multiple: false,
                    clearable: true,
                    items: ['recommendation'].map(status => ({
                        text: `labWorkOrders.labels.${status}`,
                        value: false,
                    })),
                },
            ] : this.filters;
        },
        headerActionButtons() {
            return [
                {
                    label: '',
                    action: () => {
                        this.showExportModal();
                    },
                    icon: 'save_alt',
                    color: 'secondary',
                    desktopAttributes: {
                        fab: true,
                        xSmall: true,
                    },
                    desktopExtraClass: 'iconAction',
                },
            ];
        },
        actions() {
            const actions = [];

            if (this.hasPermission('workOrders.edit')) {
                actions.push({
                    mode: 'edit',
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                });
            }
            if (this.hasPermission('workOrders.export')) {
                actions.push({
                    mode: 'export',
                    name: this.$t('forms.btn.worksheet'),
                    icon: 'mdi-card-text-outline',
                    customValidationProperty: 'showWorkSheet',
                    showOncustomValidationProperty: true,
                });
            }

            if (this.hasPermission('workOrders.remove')) {
                actions.push({
                    mode: 'remove',
                    name: this.$t('forms.btn.delete'),
                    icon: 'clear',
                    customValidationProperty: 'nbAnalysis',
                    showOncustomValidationProperty: false,
                });
            }


            return actions;
        },
    },
    async created() {
        this.isInitializing = true;
        this.getFilterValues();
        await this.getWorkOrders();
        this.isInitializing = false;
    },
    methods: {
        async getWorkOrders() {
            if (!this.loading) {
                this.loading = true;
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                const { data } = await this.$rq.getLabWorkOrders(cleanQueryParams);
                this.workOrders = [...this.workOrders, ...data.labWorkOrders];
                this.pagination = data.pagination;
                this.loading = false;
            }
        },
        getAssignedTo(user) {
            if (!user.firstName && !user.lastName) {
                return null;
            }
            const { firstName } = user || {};
            const { lastName } = user || {};
            return `${firstName} ${lastName}`;
        },
        async onClickRedirect(workOrder) {
            await this.$router.push({
                name: 'labWorkOrderView',
                params: {
                    labWorkOrderId: workOrder.id,
                    clientId: workOrder.client.id,
                    siteId: workOrder.site.id,
                },
            });
        },
        async fetchQuery(queryParams) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    order: this.preparOrderQueryParams(queryParams.order),
                    page: 1,
                };
                this.pagination = {};
                this.workOrders = [];
                await this.getWorkOrders();
            }
        },

        async getWorksheetPDF(workOrder) {
            this.isDownloadingReport = true;
            const { data } = await this.$rq.getLabWorkOrderWorkSheet(workOrder.id);
            const fileName = `${this.$t('visitSummary.worksheet', { date: (new Date()).getTime() })}.pdf`;

            this.$utils.saveFileOnDisk(data, fileName);

            this.isDownloadingReport = false;
        },

        onClickAction(item, action) {
            if (action.mode === 'edit') {
                this.onClickRedirect(item);
            } else if (action.mode === 'remove') {
                this.removeWorkOrder(item);
            } else if (action.mode === 'export') {
                this.getWorksheetPDF(item);
            }
        },
        async removeWorkOrder(item) {
            this.$root.$emit('showModal', {
                title: this.$t('workOrder.remove').toUpperCase(),
                component: Vue.component('DeleteForm', DeleteForm),
                buttonTitle: 'delete',
                payload: { msg: this.$t('workOrder.msg.remove'), security: this.$t('workOrder.msg.security') },
                isForm: true,
                onConfirm: async () => {
                    await this.$rq.removeLabWorkOrder(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.workOrderDeleted', { data: item.contractNumber }),
                    });
                    await this.fetchQuery({});
                },
            });
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'clientname':
                    newValue = 'site.name';
                    break;
                case 'clientsite':
                    newValue = 'client.name';
                    break;
                case 'creationdate':
                    newValue = 'createdAt';
                    break;
                case 'assignedto':
                    newValue = 'assignedTo.user.profile.firstName';
                    break;
                case 'workorder':
                    newValue = 'woNumber';
                    break;
                case 'ponumber':
                    newValue = 'poNumber';
                    break;
                case 'sonumber':
                    newValue = 'soNumber';
                    break;
                case 'status':
                    newValue = 'status';
                    break;
                case 'nbanalysis':
                    newValue = 'nbAnalysisRequests';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        componentName(item, prop) {
            const component = prop.item.componentInfo.find(x => x.id === item.component.id);
            if (component) {
                return component.name;
            }
            return this.$t('labWorkOrders.noComponent');
        },
        async unlink(item) {
            const workOrder = this.workOrders.find(x => x.id === item.labWorkOrder.id);
            if (workOrder) {
                this.$root.$emit('showModal', {
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('labWorkOrders.detach', { lab: item.requestNumber, workOrder: workOrder.woNumber }) },
                    onConfirm: () => this.removeAnalysisRequest(item),
                    isForm: false,
                });
            } else {
                this.$toasted.global.toast_error({
                    message: this.$t('labWorkOrders.errors.noWordOrder'),
                });
            }
        },
        async removeAnalysisRequest(item) {
            await this.$rq.removeAnalysisRequestionFromLabWorkOrder(item.labWorkOrder.id, item.id);
            this.$toasted.global.toast_success({
                message: this.$t('labWorkOrders.detachSucces'),
            });
            await this.fetchQuery({});
        },
        handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
                search: this.queryParams?.search || '',
                order: this.queryParams?.order || '',
            };

            // reset filter if needed
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }


            this.queryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.fetchQuery({});
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            this.queryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.fetchQuery({});
        },
        showExportModal() {
            this.$root.$emit('showModal', {
                title: this.$t('laboratory.export'),
                showConfirmButton: false,
                component: Vue.component('Exportation', Exportation),
            });
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getWorkOrders();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .material-icons-outlined {
        transform: rotate(90deg);
    }
</style>
